import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import React from "react";

const textVariants = cva("", {
  variants: {
    variant: {
      // 11px · uppercase · tracked · ink-4 — section labels, category chips, table headers
      eyebrow: "text-eyebrow font-medium uppercase tracking-eyebrow text-ink-4",
      // 12px · ink-4 — stat labels, breadcrumbs, save status, tiny form metadata
      meta: "text-meta text-ink-4",
      // Page/card headings — rem-relative; sizes shown at 16px root.
      "heading-1": "text-[1.875rem] font-semibold leading-[1.08] tracking-tight text-ink", // 30px
      "heading-2": "text-[1.5rem] font-semibold leading-snug tracking-tight text-ink",     // 24px
      "heading-3": "text-[1.25rem] font-semibold tracking-tight text-ink",                  // 20px
      "heading-4": "text-[1.125rem] font-semibold tracking-tight text-ink",                 // 18px
      // primary body — matches root font-size
      body: "text-body leading-[1.75] text-ink",
      // 13px secondary body / metadata
      caption: "text-caption leading-[1.8] text-ink-3",
      // Standard sm muted — helper text, descriptions
      muted: "text-sm leading-6 text-ink-3",
      // Form labels, table headers
      label: "text-sm font-medium text-ink",
    },
  },
  defaultVariants: {
    variant: "body",
  },
});

export type TextVariant = NonNullable<VariantProps<typeof textVariants>["variant"]>;

type TextOwnProps = VariantProps<typeof textVariants> & {
  as?: React.ElementType;
  className?: string;
  children?: React.ReactNode;
};

type TextProps<T extends React.ElementType = "p"> = TextOwnProps &
  Omit<React.ComponentPropsWithoutRef<T>, keyof TextOwnProps>;

export function Text<T extends React.ElementType = "p">({
  as,
  variant,
  className,
  ...props
}: TextProps<T>) {
  const Tag = (as ?? "p") as React.ElementType;
  return <Tag className={cn(textVariants({ variant }), className)} {...props} />;
}

export { textVariants };
